import { useState, useEffect } from 'react';
import { format, subDays, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { detectionAPI, reservationsAPI, tablesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  Users,
  Clock,
  Flag,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  CheckCircle,
  XCircle,
  Check,
  X,
  MoreVertical,
  Settings
} from 'lucide-react';

export default function Detection() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null); // null = all dates
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  
  // For Accept/Cancel functionality
  const [tables, setTables] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignReservation, setAssignReservation] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState(null);

  useEffect(() => {
    fetchAlerts();
    fetchTables();
  }, [selectedDate]);

  const fetchTables = async () => {
    try {
      const res = await tablesAPI.list();
      setTables(res.data || []);
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await detectionAPI.getMultiReservations(selectedDate);
      setAlerts(res.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  const openFlagDialog = (alert) => {
    setSelectedAlert(alert);
    setFlagReason('');
    setShowFlagDialog(true);
  };

  const handleFlagCustomer = async () => {
    if (!selectedAlert || !flagReason) {
      toast.error('Ingresa una razón');
      return;
    }

    try {
      // Get email or phone from first reservation
      const firstRes = selectedAlert.reservations[0];
      const identifier = selectedAlert.customer_identifier;
      
      await detectionAPI.flagCustomer(
        identifier.includes('@') ? identifier : null,
        identifier.includes('@') ? null : identifier,
        flagReason
      );
      
      toast.success(`Cliente ${selectedAlert.customer_name} marcado como problemático`);
      setShowFlagDialog(false);
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar cliente');
    }
  };

  // Handle Accept reservation (auto or manual)
  const handleAccept = async (reservation, mode = 'auto') => {
    if (mode === 'manual') {
      setAssignReservation(reservation);
      setSelectedTableId(null);
      setShowAssignDialog(true);
    } else {
      // Auto-assign: find available table
      try {
        const availableTable = tables.find(t => 
          t.capacity_max >= reservation.party_size && 
          t.status !== 'occupied'
        );
        
        if (availableTable) {
          await reservationsAPI.update(reservation.id, {
            status: 'confirmed',
            table_ids: [availableTable.id]
          });
          toast.success(`Reserva confirmada - Mesa ${availableTable.name} asignada`);
          fetchAlerts();
        } else {
          toast.error('No hay mesas disponibles - Selecciona manualmente');
          setAssignReservation(reservation);
          setSelectedTableId(null);
          setShowAssignDialog(true);
        }
      } catch (error) {
        toast.error('Error al confirmar reserva');
      }
    }
  };

  // Handle manual table assignment
  const handleManualAssign = async () => {
    if (!assignReservation || !selectedTableId) {
      toast.error('Selecciona una mesa');
      return;
    }
    
    try {
      await reservationsAPI.update(assignReservation.id, {
        status: 'confirmed',
        table_ids: [selectedTableId]
      });
      toast.success('Reserva confirmada y mesa asignada');
      setShowAssignDialog(false);
      setAssignReservation(null);
      setSelectedTableId(null);
      fetchAlerts();
    } catch (error) {
      toast.error('Error al asignar mesa');
    }
  };

  // Handle Cancel reservation
  const handleCancel = async (reservation) => {
    try {
      await reservationsAPI.update(reservation.id, { status: 'cancelled' });
      toast.success('Reserva cancelada');
      fetchAlerts();
    } catch (error) {
      toast.error('Error al cancelar reserva');
    }
  };

  const goToDate = (days) => {
    if (selectedDate) {
      setSelectedDate(format(addDays(new Date(selectedDate), days), 'yyyy-MM-dd'));
    } else {
      setSelectedDate(format(addDays(new Date(), days), 'yyyy-MM-dd'));
    }
  };

  const highSeverityCount = alerts.filter(a => a.severity === 'high').length;
  const mediumSeverityCount = alerts.filter(a => a.severity === 'medium').length;

  return (
    <div className="min-h-screen" data-testid="detection-page">
      <TopBar
        title="Detección de Multi-Reservaciones"
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/settings?tab=alerts')}
              className="rounded-xl"
              data-testid="configure-alerts-btn"
            >
              <Settings className="h-4 w-4 mr-1" /> Configurar Alertas
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchAlerts}
              className="rounded-xl"
              data-testid="refresh-detection-btn"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        }
      />

      <div className="p-6">
        {/* Stats and Date Filter */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-xl bg-red-500/10">
                  <ShieldAlert className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{alerts.length}</p>
                  <p className="text-xs text-muted-foreground">Alertas totales</p>
                </div>
              </CardContent>
            </Card>
            
            {highSeverityCount > 0 && (
              <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-xl font-bold text-red-500">{highSeverityCount}</p>
                    <p className="text-xs text-muted-foreground">Alta severidad</p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {mediumSeverityCount > 0 && (
              <Card className="border-0 shadow-sm border-l-4 border-l-yellow-500">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  <div>
                    <p className="text-xl font-bold text-yellow-500">{mediumSeverityCount}</p>
                    <p className="text-xs text-muted-foreground">Media severidad</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => goToDate(-1)}
              className="rounded-xl"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="rounded-xl min-w-[180px]" data-testid="date-filter-btn">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {selectedDate 
                    ? format(new Date(selectedDate), 'PPP', { locale: es })
                    : 'Todas las fechas'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start mb-2"
                    onClick={() => setSelectedDate(null)}
                  >
                    Todas las fechas
                  </Button>
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate ? new Date(selectedDate) : undefined}
                  onSelect={(date) => date && setSelectedDate(format(date, 'yyyy-MM-dd'))}
                  locale={es}
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              size="icon"
              onClick={() => goToDate(1)}
              className="rounded-xl"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Sistema de Detección de Multi-Reservaciones</p>
                <p className="text-sm text-muted-foreground">
                  Este sistema detecta automáticamente cuando un cliente (identificado por email o teléfono) 
                  tiene múltiples reservaciones el mismo día. Puedes marcar clientes problemáticos para 
                  futuras referencias.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="mt-4 text-muted-foreground">Analizando reservaciones...</p>
              </CardContent>
            </Card>
          ) : alerts.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="text-lg font-medium text-foreground">¡Sin alertas!</p>
                <p>No se detectaron clientes con múltiples reservaciones{selectedDate ? ' en esta fecha' : ''}.</p>
              </CardContent>
            </Card>
          ) : (
            alerts.map((alert, index) => (
              <Card 
                key={index} 
                className={cn(
                  "border-0 shadow-sm",
                  alert.severity === 'high' && "border-l-4 border-l-red-500",
                  alert.severity === 'medium' && "border-l-4 border-l-yellow-500"
                )}
                data-testid={`alert-card-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={cn(
                          "p-2 rounded-xl",
                          alert.severity === 'high' ? "bg-red-500/10" : "bg-yellow-500/10"
                        )}>
                          <AlertTriangle className={cn(
                            "h-5 w-5",
                            alert.severity === 'high' ? "text-red-500" : "text-yellow-500"
                          )} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{alert.customer_name}</h3>
                          <p className="text-sm text-muted-foreground">{alert.customer_identifier}</p>
                        </div>
                        <Badge 
                          className={cn(
                            alert.severity === 'high' ? "bg-red-500" : "bg-yellow-500"
                          )}
                        >
                          {alert.reservation_count} reservaciones
                        </Badge>
                      </div>

                      <div className="space-y-2">
                        {alert.reservations.map((res, i) => {
                          const isPending = res.status === 'pending';
                          return (
                          <div 
                            key={res.id} 
                            className={cn(
                              "flex items-center justify-between p-3 bg-muted rounded-xl text-sm",
                              isPending && "border-l-4 border-l-yellow-400"
                            )}
                          >
                            <div className="flex items-center gap-4">
                              <span className="font-mono text-muted-foreground">#{i + 1}</span>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{res.time}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span>{res.party_size} personas</span>
                              </div>
                              <Badge variant="outline" className={cn(
                                res.status === 'pending' && "bg-yellow-100 text-yellow-700 border-yellow-300",
                                res.status === 'confirmed' && "bg-green-100 text-green-700 border-green-300",
                                res.status === 'cancelled' && "bg-red-100 text-red-700 border-red-300"
                              )}>
                                {res.status === 'pending' && 'Pendiente'}
                                {res.status === 'confirmed' && 'Confirmada'}
                                {res.status === 'seated' && 'Sentado'}
                                {res.status === 'cancelled' && 'Cancelada'}
                              </Badge>
                            </div>
                            
                            {/* Action buttons for each reservation */}
                            {isPending ? (
                              <div className="flex items-center gap-1">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="rounded-lg h-7 text-green-600 border-green-300 hover:bg-green-50"
                                      data-testid={`accept-res-${res.id}`}
                                    >
                                      <Check className="h-3 w-3 mr-1" /> Aceptar
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleAccept(res, 'auto')}>
                                      <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                                      Automática
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleAccept(res, 'manual')}>
                                      <MoreVertical className="h-4 w-4 mr-2 text-blue-500" />
                                      Manual
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleCancel(res)}
                                  className="rounded-lg h-7 text-red-600 border-red-300 hover:bg-red-50"
                                  data-testid={`cancel-res-${res.id}`}
                                >
                                  <X className="h-3 w-3 mr-1" /> Cancelar
                                </Button>
                              </div>
                            ) : res.status !== 'cancelled' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleCancel(res)}
                                className="rounded-lg h-7 text-red-600 hover:bg-red-50"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )})}
                      </div>

                      <p className="text-sm text-muted-foreground mt-3 flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4" />
                        Fecha: {format(new Date(alert.date), 'PPP', { locale: es })}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openFlagDialog(alert)}
                        className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`flag-customer-${index}`}
                      >
                        <Flag className="h-4 w-4 mr-1" /> Marcar cliente
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Flag Customer Dialog */}
      <Dialog open={showFlagDialog} onOpenChange={setShowFlagDialog}>
        <DialogContent className="rounded-2xl" data-testid="flag-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Flag className="h-5 w-5" /> Marcar Cliente como Problemático
            </DialogTitle>
          </DialogHeader>

          {selectedAlert && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-xl">
                <p className="font-medium">{selectedAlert.customer_name}</p>
                <p className="text-sm text-muted-foreground">{selectedAlert.customer_identifier}</p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  {selectedAlert.reservation_count} reservaciones el {format(new Date(selectedAlert.date), 'PPP', { locale: es })}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">Razón del marcado *</label>
                <Textarea
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  placeholder="Ej: Cliente con múltiples reservaciones sin asistir, posible abuso del sistema..."
                  rows={3}
                  className="rounded-xl mt-2"
                  data-testid="flag-reason-textarea"
                />
              </div>

              <div className="p-3 bg-muted rounded-xl text-sm">
                <p className="font-medium mb-1">¿Qué sucederá?</p>
                <ul className="text-muted-foreground space-y-1">
                  <li>• Se agregará la etiqueta "Flagged" al cliente</li>
                  <li>• Se registrará la razón en las notas del cliente</li>
                  <li>• El cliente seguirá visible pero con advertencia</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlagDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button 
              onClick={handleFlagCustomer} 
              disabled={!flagReason}
              className="rounded-xl bg-red-500 hover:bg-red-600"
              data-testid="confirm-flag-btn"
            >
              <Flag className="h-4 w-4 mr-1" /> Marcar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Table Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="rounded-2xl" data-testid="assign-table-dialog">
          <DialogHeader>
            <DialogTitle>Asignar Mesa Manualmente</DialogTitle>
          </DialogHeader>

          {assignReservation && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-xl">
                <p className="font-medium">{assignReservation.customer_name}</p>
                <p className="text-sm text-muted-foreground">
                  {assignReservation.time} · {assignReservation.party_size} personas
                </p>
              </div>

              <div>
                <Label>Seleccionar mesa</Label>
                <Select value={selectedTableId || ''} onValueChange={setSelectedTableId}>
                  <SelectTrigger className="rounded-xl" data-testid="select-table">
                    <SelectValue placeholder="Selecciona una mesa" />
                  </SelectTrigger>
                  <SelectContent>
                    {tables
                      .filter(t => t.capacity_max >= (assignReservation?.party_size || 1))
                      .map(table => (
                        <SelectItem key={table.id} value={table.id}>
                          {table.name} (Cap. {table.capacity_min}-{table.capacity_max})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button 
              onClick={handleManualAssign}
              disabled={!selectedTableId}
              className="rounded-xl"
              data-testid="confirm-assign-btn"
            >
              <Check className="h-4 w-4 mr-1" /> Confirmar y Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
